import { getToken } from './auth';

const apiBase = import.meta.env.ECOS_API_BASE;

/** What the API says about a pending MCP authorization request. */
export type OAuthAuthorizationDetails = {
  clientName: string | null;
  redirectHost: string;
  server: { key: string; label: string };
  company: { id: number; name: string | null };
  user: { email: string };
  /** Present when this user may not approve the request. */
  denialReason?: string;
};

/** Thrown when the web token is missing or rejected, so the page can send the user to log in. */
export class OAuthLoginRequiredError extends Error {}

const oAuthFetch = async <T>(
  path: string,
  init: NonNullable<Parameters<typeof fetch>[1]> = {}
): Promise<T> => {
  const token = getToken();
  if (!token) {
    throw new OAuthLoginRequiredError();
  }
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: `Bearer ${token}`,
    },
  });
  if (res.status === 401) {
    throw new OAuthLoginRequiredError();
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      data.error_description || data.message || 'Something went wrong.'
    );
  }
  return data as T;
};

/** The OAuth parameters from the consent page URL, passed through unchanged. */
export const getAuthorizationParams = (search: string) =>
  Object.fromEntries(new URLSearchParams(search).entries());

export const describeAuthorization = (search: string) =>
  oAuthFetch<OAuthAuthorizationDetails>(
    `/oauth/authorize/request?${new URLSearchParams(search).toString()}`
  );

const decide = (decision: 'approve' | 'deny', search: string) =>
  oAuthFetch<{ redirectUrl: string }>(`/oauth/authorize/${decision}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(getAuthorizationParams(search)),
  });

export const approveAuthorization = (search: string) =>
  decide('approve', search);

export const denyAuthorization = (search: string) => decide('deny', search);
